"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ToastNotification = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const constants_1 = require("@/app/shared/constants");
const sanitizeHtml_1 = require("@/app/shared/helpers/sanitizeHtml");
const useToastPosition_1 = require("@/app/shared/hooks/useToastPosition");
const labels_1 = __importDefault(require("@/app/shared/i18n/labels"));
const notifications_1 = require("@/app/shared/store/notifications");
const outline_1 = require("@heroicons/react/24/outline");
const button_1 = require("primereact/button");
const toast_1 = require("primereact/toast");
const utils_1 = require("primereact/utils");
const react_1 = require("react");
const react_redux_1 = require("react-redux");
const ToastNotification = () => {
    const dispatch = (0, react_redux_1.useDispatch)();
    const toastNotificationsQueue = (0, react_redux_1.useSelector)(notifications_1.notificationsSelectors.selectToastNotificationsQueue);
    const { position, offset } = (0, useToastPosition_1.useToastPosition)();
    const toastNotification = (0, react_1.useRef)(null);
    const localMessages = (0, react_1.useRef)([]);
    const timersRef = (0, react_1.useRef)(new Map());
    const severityToTitle = {
        error: labels_1.default.ERROR,
        success: labels_1.default.SUCCESS,
        info: labels_1.default.INFO,
        warn: labels_1.default.WARNING,
    };
    const severityToIcon = {
        error: (0, jsx_runtime_1.jsx)(outline_1.XCircleIcon, { className: 'p-toast-message-icon' }),
        success: (0, jsx_runtime_1.jsx)(outline_1.CheckCircleIcon, { className: 'p-toast-message-icon' }),
        info: (0, jsx_runtime_1.jsx)(outline_1.InformationCircleIcon, { className: 'p-toast-message-icon' }),
        warn: (0, jsx_runtime_1.jsx)(outline_1.ExclamationCircleIcon, { className: 'p-toast-message-icon' }),
    };
    (0, react_1.useEffect)(() => {
        const toastMessages = toastNotificationsQueue.map((toastData) => {
            const { id, data, ...dynamicMessage } = toastData;
            const item = Object.assign({}, constants_1.TOAST_ERRORS_LIST[id], dynamicMessage);
            if (item.summary && data?.replaceSummaryKeys) {
                for (const key in data.replaceSummaryKeys) {
                    const value = data.replaceSummaryKeys[key] || '';
                    item.summary = item.summary.toString().replace(key, value);
                }
            }
            if (item.detail && data?.replaceDetailKeys) {
                for (const key in data.replaceDetailKeys) {
                    const value = data.replaceDetailKeys[key] || '';
                    item.detail = item.detail.toString().replace(key, value);
                }
            }
            return {
                ...item,
                summary: item.summary ? item.summary : item.severity ? severityToTitle[item.severity] : '',
                icon: item.severity ? severityToIcon[item.severity] : null,
            };
        });
        const newMessages = toastMessages.filter((message) => message.id && !localMessages.current.find((localMessage) => localMessage.id === message.id));
        if (toastNotification.current) {
            newMessages.length && toastNotification.current.show(newMessages);
        }
        newMessages.forEach((message) => {
            const id = message.id;
            if (id && message.life && !timersRef.current.has(id)) {
                const timer = setTimeout(() => {
                    dispatch(notifications_1.notificationsActions.hideToast({ id }));
                    timersRef.current.delete(id);
                }, message.life);
                timersRef.current.set(id, timer);
            }
        });
        const currentIds = new Set(toastMessages.map((m) => m.id));
        timersRef.current.forEach((timer, id) => {
            if (!currentIds.has(id)) {
                clearTimeout(timer);
                timersRef.current.delete(id);
            }
        });
        localMessages.current = toastMessages;
    }, [toastNotificationsQueue]);
    const handleClose = (toastMessage) => {
        const id = toastMessage.id;
        if (!id)
            return;
        const timer = timersRef.current.get(id);
        if (timer) {
            clearTimeout(timer);
            timersRef.current.delete(id);
        }
        dispatch(notifications_1.notificationsActions.hideToast({ id }));
    };
    const contentTemplate = (props) => {
        const message = props.message;
        const Icon = message.icon;
        return ((0, jsx_runtime_1.jsxs)("section", { className: (0, utils_1.classNames)(message.severity && `p-toast-message p-toast-message-${message.severity}`, 'flex items-center p-16px border-1 border-gray-20 border-radius-8px shadow-level-2 bg-white gap-12px max-w-360px'), children: [Icon, (0, jsx_runtime_1.jsxs)("div", { className: 'p-toast-message-text flex flex-column gap-4px', children: [(0, jsx_runtime_1.jsx)("div", { className: 'p-toast-summary text-title-xs-med text-gray-100', children: message.summary }), (0, jsx_runtime_1.jsx)("div", { className: 'p-toast-detail text-body-s-reg text-gray-80', dangerouslySetInnerHTML: { __html: (0, sanitizeHtml_1.sanitizeHtml)(typeof message.detail === 'string' ? message.detail : '') } })] }), (0, jsx_runtime_1.jsx)(button_1.Button, { className: 'p-toast-icon-close p-link text-gray-60 border-none border-radius-full p-0 min-w-20px max-w-20px h-20px', icon: (0, jsx_runtime_1.jsx)(outline_1.XMarkIcon, { className: 'p-icon p-toast-icon-close-icon w-20px h-20px' }), onClick: () => handleClose(message), link: true })] }));
    };
    return ((0, jsx_runtime_1.jsx)(toast_1.Toast, { ref: toastNotification, position: position, pt: { root: { className: (0, utils_1.classNames)('w-fit-content', offset) } }, onRemove: handleClose, content: contentTemplate }));
};
exports.ToastNotification = ToastNotification;
